import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

// This component handles the OAuth callback redirect to the native app
const AuthCallback = () => {
  const [debugInfo, setDebugInfo] = useState(['Loading Clerk session...']);
  const [attempts, setAttempts] = useState(0);
  const [sessionToken, setSessionToken] = useState(null);
  const { isLoaded, isSignedIn, getToken, sessionId } = useAuth();

  useEffect(() => {
    const checkClerkSession = async () => {
      const info = [];
      info.push('Time: ' + new Date().toLocaleTimeString());
      info.push('Clerk loaded: ' + isLoaded);
      info.push('Clerk signed in: ' + isSignedIn);
      info.push('Session ID: ' + (sessionId || 'none'));

      if (isLoaded && isSignedIn) {
        try {
          // Get the session token from Clerk
          const token = await getToken();
          info.push('Token obtained: ' + (token ? 'YES (' + token.substring(0, 20) + '...)' : 'NO'));
          setSessionToken(token);

          // Auto-redirect with token after getting it
          setTimeout(() => {
            openAppWithToken(token, sessionId);
          }, 1000);
        } catch (err) {
          info.push('Token error: ' + err.message);
        }
      } else if (isLoaded && !isSignedIn) {
        info.push('Not signed in - checking URL params...');

        // Fallback: check URL params
        const searchParams = new URLSearchParams(window.location.search);
        const urlSessionId = searchParams.get('__clerk_created_session') || searchParams.get('__clerk_session');
        info.push('URL session: ' + (urlSessionId || 'none'));
      }

      setDebugInfo(info);
    };

    checkClerkSession();
  }, [isLoaded, isSignedIn, getToken, sessionId]);

  const openAppWithToken = (token, sessId) => {
    // Build URL with token for the native app
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (sessId) params.set('session_id', sessId);

    const customSchemeUrl = 'com.runium.app://auth-callback?' + params.toString();
    setAttempts(prev => prev + 1);
    setDebugInfo(prev => [...prev, `Opening app with token at ${new Date().toLocaleTimeString()}`]);
    window.location.href = customSchemeUrl;
  };

  const openApp = () => {
    if (sessionToken) {
      openAppWithToken(sessionToken, sessionId);
    } else {
      // Fallback without token
      const customSchemeUrl = 'com.runium.app://auth-callback' + window.location.search;
      setAttempts(prev => prev + 1);
      setDebugInfo(prev => [...prev, `Opening app (no token) at ${new Date().toLocaleTimeString()}`]);
      window.location.href = customSchemeUrl;
    }
  };

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      margin: 0,
      padding: '20px',
      boxSizing: 'border-box',
      textAlign: 'center',
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid rgba(59, 130, 246, 0.3)',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>Sign-in Successful!</h1>
      <p style={{ color: '#10b981', fontWeight: 'bold', margin: '15px 0' }}>
        {attempts === 0 ? 'Preparing to open app...' : `Opening app (attempt ${attempts})...`}
      </p>
      <p style={{ color: '#9ca3af', fontSize: '14px', margin: '5px 0' }}>
        Tap the button below to return to Runium
      </p>

      <button
        onClick={() => openApp()}
        style={{
          color: 'white',
          background: '#3b82f6',
          textDecoration: 'none',
          marginTop: '20px',
          padding: '16px 32px',
          border: 'none',
          borderRadius: '8px',
          display: 'inline-block',
          fontSize: '18px',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Open Runium App
      </button>

      <div style={{
        marginTop: '30px',
        padding: '15px',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '8px',
        fontFamily: 'monospace',
        fontSize: '11px',
        maxWidth: '100%',
        wordBreak: 'break-all',
        textAlign: 'left',
      }}>
        {debugInfo.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );
};

export default AuthCallback;
